import React, { useState, useEffect, useContext } from "react";
import { getAllRisksWithFilters, deleteRisk } from "../api/Api";
import { showToast } from "../utils/toastUtils";
import HeaderAndFilters from "./HeadersAndFilters";
import { LuSquareAsterisk } from "react-icons/lu";
import AddNewRisk from "./modals/AddNewRisk";
import { ProjectContext } from "../contexts/ProjectContextProvider";
import ListItems from "./ListItems";
import UpdateRiskModel from "./modals/UpdateRiskModel";
import { UserDetailsSchema } from './../utils/validations';

function RisksForWorkStream({ 
  organizationId,
  workStreamId,
  setShowAddRiskModal,
  showAddRiskModal,
  handleAddRisk,
  newRisks
  }) {
  const { data: projectData } = useContext(ProjectContext);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [filteredRisks, setFilteredRisks] = useState([]);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [showUpdateRiskModal, setShowUpdateRiskModal] = useState(false);



  const projectOptions =
    projectData?.map((project) => ({
      label: project?.name, // Display name in filter dropdown
      value: project?.id, // Use id as the value to identify the selected option
    })) || [];

  // Function to fetch risks from the API and set them in the state
 const fetchRisks = async () => {
  try {
    setLoading(true);

    //  fetch ALL risks without user filter
  const allRisksResponse = await getAllRisksWithFilters(organizationId, workStreamId, null);

      if (allRisksResponse.status === 200) {
        // Create createdByOptions from full unfiltered list
        const uniqueCreatedBy = [
          ...new Set(allRisksResponse.data.map((risk) => JSON.stringify(risk.created_by))),
        ];
        const createdByOptions = uniqueCreatedBy.map((userStr) => {
          const user = JSON.parse(userStr);
          return {
            label: `${user.first_name} ${user.last_name}`,
            value: user.id,
          };
        });

        // fetch filtered risks based on selected user
        const filteredResponse = await getAllRisksWithFilters(organizationId, workStreamId, filters.user);
        if (filteredResponse.status === 200) {
          setRisks(filteredResponse.data);
          setFilters((prevFilters) => ({
            ...prevFilters,
            createdByOptions, 
          }));
          filterRisks(filteredResponse.data);
        } else {
          showToast(filteredResponse.message || "Failed to fetch risks", "error");
        }
      } else {
        showToast(allRisksResponse.message || "Failed to fetch risks", "error");
      }
    } catch (error) {
      console.error("Error fetching risks:", error);
      showToast("Failed to fetch risks", "error");
      setError("Failed to fetch risks. Please try again later.");
    } finally {
      setLoading(false);
    }
  };


  // Function to filter risks based on the search input
  const filterRisks = (data) => {
    let filtered = data;
    if (filters.search) {
      filtered = data.filter((risk) => {
        const name = risk.title ? risk.title.toLowerCase() : "";
        const description = risk.description ? risk.description.toLowerCase() : "";
        return name.includes(filters.search.toLowerCase()) || description.includes(filters.search.toLowerCase());
      });
    }
    setFilteredRisks(filtered);
  };

  // Function to refresh data by re-fetching risks and applying filters
  const refresh = () => {
    fetchRisks();
  };

  // Initial fetch when the component mounts or when organization/workStreamId changes
  useEffect(() => {
    if (organizationId && workStreamId) {
      fetchRisks();
    }
  }, [organizationId, workStreamId,filters.user]);

  // Reapply filters whenever filters or risks change
  useEffect(() => {
    filterRisks(risks);
  }, [filters.search, risks]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }



  // Handle deleting a risk
  const handleDelete = async (riskId) => {
    try {
      const response = await deleteRisk(riskId);
      if (response.status === 200) {
        showToast("Risk deleted successfully", "success");
        setRisks((prevRisks) => prevRisks.filter((risk) => risk.id !== riskId));
        refresh(); 
      } else {
        showToast(response.message || "Failed to delete risk", "error");
      }
    } catch (error) {
      showToast("Failed to delete risk", "error");
      console.error("Error deleting risk:", error);
    }
  };

  // Handle filter change
  const handleFilterChange = (newFilters) => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  
  const openAddRiskModal = () => setShowAddRiskModal(true);
  const closeAddRiskModal = () => setShowAddRiskModal(false);
  // Open update modal
  const openUpdateRiskModal = (risk) => {
    setSelectedRisk(risk);
    setShowUpdateRiskModal(true);
  };

  // Close update modal
  const closeUpdateRiskModal = () => {
    setSelectedRisk(null);
    setShowUpdateRiskModal(false);
  };

  return (
    <div>
      <HeaderAndFilters
        filters={filters}
        showHeader={false}
        headerIcon={<LuSquareAsterisk  size={35} />}
        headerTitle={<span style={{ fontSize: '20px' }}>Risk List</span>}
        projectOptions={projectOptions}
        workstreamOptions={[{ label: "Workstream 1", value: 1 }, { label: "Workstream 2", value: 2 }]}
        createdByOptions={filters.createdByOptions || []}
        handleClick={openAddRiskModal}
        onFilterChange={handleFilterChange}
        newButtonText="NEW RISK"
        searchPlaceholder="Search..."
        showProjectButton={false}
        showWorkstreamButton={false}
        showCreatedByButton={true}
      />
      <ListItems
        items={filteredRisks}
        onEdit={openUpdateRiskModal}
        onDelete={handleDelete}
        titleField="title"
        descriptionField="description"
        dateField="created_at"
        avatarField="created_by"
        noItemsMessage='No risks to display. To add a new risk, click on the "NEW RISK" button.'
      />
      {/* Add Risk Modal */}
      <AddNewRisk 
        show={showAddRiskModal} 
        handleClose={closeAddRiskModal} 
        onAddRisk={handleAddRisk}
        refresh={refresh} 
        workStreamId={workStreamId}
      />
      {selectedRisk && (
        <UpdateRiskModel
          show={showUpdateRiskModal}
          handleClose={closeUpdateRiskModal}
          risk={selectedRisk}
          refresh={refresh} 
          workStreamId= {workStreamId}
        />
      )}
    </div>
  );
}

export default RisksForWorkStream;
